import React, { useState, useEffect, useCallback } from "react";
import {
  Collapse,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  Tag,
  Popconfirm,
  Spin,
  message,
  Empty,
  Radio,
  Select,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  LoadingOutlined,
  VideoCameraOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import {
  _get_all_ambassador_lessons_admin,
  _create_ambassador_lesson,
  _update_ambassador_lesson,
  _delete_ambassador_lesson,
  _get_ambassador_quiz_admin,
  _create_ambassador_quiz_question,
  _update_ambassador_quiz_question,
  _delete_ambassador_quiz_question,
} from "../../../common/axios_services";

const { TextArea } = Input;

const AdminAmbassadorLessons = () => {
  const [activeSubTab, setActiveSubTab] = useState("lessons");
  const [lessons, setLessons] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [quizLoading, setQuizLoading] = useState(false);
  const [selectedLessonFilter, setSelectedLessonFilter] = useState("ALL");

  const [modalOpen, setModalOpen] = useState(false);
  const [editingLesson, setEditingLesson] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [previewVideoUrl, setPreviewVideoUrl] = useState(null);
  const [form] = Form.useForm();

  const [quizModalOpen, setQuizModalOpen] = useState(false);
  const [editingQuiz, setEditingQuiz] = useState(null);
  const [quizSubmitting, setQuizSubmitting] = useState(false);
  const [quizForm] = Form.useForm();

  const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

  const fetchLessons = useCallback(async () => {
    setLoading(true);
    try {
      const response = await _get_all_ambassador_lessons_admin();
      const raw =
        response?.data?.data?.data ||
        response?.data?.data ||
        response?.data?.lessons ||
        response?.data ||
        [];
      const list = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.data)
          ? raw.data
          : Array.isArray(raw?.lessons)
            ? raw.lessons
            : [];
      setLessons(list);
    } catch (error) {
      message.error("Failed to fetch Ambassador lessons");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchQuizzes = useCallback(async () => {
    setQuizLoading(true);
    try {
      const response = await _get_ambassador_quiz_admin();
      const raw =
        response?.data?.data?.data ||
        response?.data?.data ||
        response?.data?.quiz ||
        response?.data ||
        [];
      const list = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.data)
          ? raw.data
          : [];
      setQuizzes(list);
    } catch (error) {
      message.error("Failed to fetch Ambassador quiz questions");
    } finally {
      setQuizLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLessons();
    fetchQuizzes();
  }, [fetchLessons, fetchQuizzes]);

  const openCreateModal = () => {
    setEditingLesson(null);
    form.resetFields();
    form.setFieldsValue({
      isActive: true,
      orderIndex: (lessons.length || 0) + 1,
    });
    setModalOpen(true);
  };

  const openEditModal = (lesson) => {
    setEditingLesson(lesson);
    form.setFieldsValue({
      title: lesson.title,
      description: lesson.description,
      videoUrl: lesson.videoUrl,
      orderIndex: lesson.orderIndex,
      isActive: lesson.isActive,
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      if (editingLesson) {
        await _update_ambassador_lesson(editingLesson.id, values);
        message.success("Ambassador lesson updated successfully");
      } else {
        await _create_ambassador_lesson(values);
        message.success("Ambassador lesson created successfully");
      }

      setModalOpen(false);
      form.resetFields();
      setEditingLesson(null);
      fetchLessons();
    } catch (error) {
      if (error?.errorFields) return;
      message.error("Failed to save Ambassador lesson");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await _delete_ambassador_lesson(id);
      message.success("Lesson deleted successfully");
      fetchLessons();
    } catch (error) {
      message.error("Failed to delete lesson");
    }
  };

  const openCreateQuizModal = () => {
    setEditingQuiz(null);
    quizForm.resetFields();
    quizForm.setFieldsValue({
      lessonId: selectedLessonFilter !== "ALL" ? selectedLessonFilter : (lessons[0]?.id || null),
      points: 2,
      orderIndex: (quizzes.length || 0) + 1,
      correctAnswer: 0,
      isActive: true,
      option0: "",
      option1: "",
      option2: "",
      option3: "",
    });
    setQuizModalOpen(true);
  };

  const openEditQuizModal = (quiz) => {
    setEditingQuiz(quiz);
    const options = Array.isArray(quiz.options)
      ? quiz.options
      : typeof quiz.options === "string"
        ? JSON.parse(quiz.options || "[]")
        : [];

    quizForm.setFieldsValue({
      lessonId: quiz.lessonId || null,
      question: quiz.question,
      points: quiz.points !== undefined && quiz.points !== null ? Number(quiz.points) : 2,
      option0: options[0] || "",
      option1: options[1] || "",
      option2: options[2] || "",
      option3: options[3] || "",
      correctAnswer: Number(quiz.correctAnswer ?? 0),
      explanation: quiz.explanation || "",
      orderIndex: quiz.orderIndex !== undefined ? Number(quiz.orderIndex) : 1,
      isActive: Boolean(quiz.isActive ?? true),
    });
    setQuizModalOpen(true);
  };

  const handleQuizSubmit = async () => {
    try {
      const values = await quizForm.validateFields();
      setQuizSubmitting(true);

      const payload = {
        lessonId: values.lessonId || null,
        question: values.question.trim(),
        options: [
          values.option0.trim(),
          values.option1.trim(),
          values.option2.trim(),
          values.option3.trim(),
        ],
        correctAnswer: Number(values.correctAnswer),
        points: values.points !== undefined && values.points !== null ? Number(values.points) : 2,
        explanation: (values.explanation || "").trim(),
        orderIndex: values.orderIndex !== undefined ? Number(values.orderIndex) : 1,
        isActive: Boolean(values.isActive),
      };

      if (editingQuiz) {
        await _update_ambassador_quiz_question(editingQuiz.id, payload);
        message.success("Quiz question updated successfully");
      } else {
        await _create_ambassador_quiz_question(payload);
        message.success("Quiz question created successfully with assigned points");
      }

      setQuizModalOpen(false);
      quizForm.resetFields();
      setEditingQuiz(null);
      fetchQuizzes();
    } catch (error) {
      if (error?.errorFields) return;
      message.error("Failed to save quiz question");
    } finally {
      setQuizSubmitting(false);
    }
  };

  const handleQuizDelete = async (id) => {
    try {
      await _delete_ambassador_quiz_question(id);
      message.success("Quiz question deleted successfully");
      fetchQuizzes();
    } catch (error) {
      message.error("Failed to delete quiz question");
    }
  };

  const renderLessonCollapseItems = () => {
    return lessons.map((lesson, idx) => ({
      key: lesson.id || idx,
      label: (
        <div className="flex justify-between items-center w-full pr-4">
          <div className="flex items-center gap-2">
            <Tag color="green" className="font-gilroyBold">
              Lesson #{lesson.orderIndex || idx + 1}
            </Tag>
            <span className="font-gilroyBold text-gray-900 truncate">
              {lesson.title}
            </span>
          </div>
          <Tag color={lesson.isActive ? "green" : "red"}>
            {lesson.isActive ? "Active" : "Inactive"}
          </Tag>
        </div>
      ),
      children: (
        <div>
          <ul className="mobile-list-flex space-y-2">
            <li>
              <span className="font-gilroyBold">Title:</span>
              <span className="font-medium text-gray-800">{lesson.title}</span>
            </li>
            <li className="flex-col !items-start">
              <span className="font-gilroyBold">Description:</span>
              <div className="mt-1 text-gray-600 w-full ">
                {lesson.description || "No description provided."}
              </div>
            </li>
            <li>
              <span className="font-gilroyBold">Video URL:</span>
              <div className="flex items-center gap-2">
                <a
                  href={lesson.videoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-emerald-600 hover:text-emerald-700 underline truncate max-w-xs"
                >
                  {lesson.videoUrl}
                </a>
                {lesson.embedUrl && (
                  <Button
                    size="small"
                    type="link"
                    icon={<EyeOutlined />}
                    onClick={() => setPreviewVideoUrl(lesson.embedUrl)}
                  >
                    Preview
                  </Button>
                )}
              </div>
            </li>
            <li>
              <span className="font-gilroyBold">Display Order:</span>
              <span className="font-gilroyBold text-emerald-700">
                #{lesson.orderIndex}
              </span>
            </li>
            <li>
              <span className="font-gilroyBold">Status:</span>
              <Tag color={lesson.isActive ? "green" : "red"}>
                {lesson.isActive ? "Active (Visible to Ambassadors)" : "Inactive"}
              </Tag>
            </li>
            <li className="mt-4 !border-b-0 pt-2">
              <span className="font-gilroyBold">Actions:</span>
              <div className="flex gap-2">
                <Button
                  size="small"
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={() => openEditModal(lesson)}
                  className="bg-emerald-600 hover:bg-emerald-500 font-gilroyBold rounded-xl"
                >
                  Edit
                </Button>
                <Popconfirm
                  title="Delete this Ambassador lesson?"
                  onConfirm={() => handleDelete(lesson.id)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button size="small" danger icon={<DeleteOutlined />} className="rounded-xl">
                    Delete
                  </Button>
                </Popconfirm>
              </div>
            </li>
          </ul>
        </div>
      ),
    }));
  };

  const renderQuizCollapseItems = () => {
    const displayedQuizzes =
      selectedLessonFilter === "ALL"
        ? quizzes
        : quizzes.filter((q) => q.lessonId === selectedLessonFilter);

    return displayedQuizzes.map((quiz, idx) => {
      const options = Array.isArray(quiz.options)
        ? quiz.options
        : typeof quiz.options === "string"
          ? JSON.parse(quiz.options || "[]")
          : [];

      const matchedLesson = lessons.find((l) => l.id === quiz.lessonId);

      return {
        key: quiz.id || idx,
        label: (
          <div className="flex justify-between items-center w-full pr-4">
            <div className="flex items-center gap-2 min-w-0">
              <Tag color="blue" className="font-gilroyBold flex-shrink-0">
                Q#{quiz.orderIndex || idx + 1}
              </Tag>
              {matchedLesson ? (
                <Tag color="cyan" className="font-gilroyBold flex-shrink-0">
                  {matchedLesson.title}
                </Tag>
              ) : (
                <Tag color="default" className="font-gilroyBold flex-shrink-0">
                  Unassigned
                </Tag>
              )}
              <span className="font-gilroyBold text-gray-900 truncate">
                {quiz.question}
              </span>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Tag color="gold" className="font-gilroyBold">
                {quiz.points !== undefined && quiz.points !== null ? quiz.points : 2} Points
              </Tag>
              <Tag color={quiz.isActive ? "green" : "red"}>
                {quiz.isActive ? "Active" : "Inactive"}
              </Tag>
            </div>
          </div>
        ),
        children: (
          <div>
            <div className="space-y-4">
              <div>
                <span className="font-gilroyBold text-gray-900  block mb-1">
                  Question Prompt:
                </span>
                <p className="text-gray-800  leading-relaxed m-0 bg-gray-50 p-3 rounded-xl border border-gray-200">
                  {quiz.question}
                </p>
              </div>

              <div>
                <span className="font-gilroyBold text-gray-900  block mb-2">
                  Answer Options:
                </span>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                  {options.map((opt, optIdx) => {
                    const isCorrect = Number(quiz.correctAnswer) === optIdx;
                    return (
                      <div
                        key={optIdx}
                        className={`p-3 rounded-xl border flex items-center justify-between gap-2  ${isCorrect
                          ? "bg-emerald-50 border-emerald-300 text-emerald-900 font-gilroyBold ring-1 ring-emerald-400"
                          : "bg-white border-gray-200 text-gray-700"
                          }`}
                      >
                        <div className="flex items-center gap-2">
                          <span
                            className={`w-6 h-6 rounded-lg flex items-center justify-center  font-gilroyBold ${isCorrect
                              ? "bg-emerald-600 text-white"
                              : "bg-gray-100 text-gray-600"
                              }`}
                          >
                            {String.fromCharCode(65 + optIdx)}
                          </span>
                          <span>{opt}</span>
                        </div>
                        {isCorrect && (
                          <Tag color="green" className="font-gilroyBold m-0 ">
                            Correct
                          </Tag>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {quiz.explanation && (
                <div>
                  <span className="font-gilroyBold text-gray-900  block mb-1">
                    Explanation Note:
                  </span>
                  <div className="text-gray-700  bg-blue-50/50 p-3 rounded-xl border border-blue-100">
                    {quiz.explanation}
                  </div>
                </div>
              )}

              <div className="flex flex-wrap items-center justify-between gap-4 pt-2 border-t border-gray-100">
                <div className="flex items-center gap-4 ">
                  <div>
                    <span className="text-gray-500">Points: </span>
                    <strong className="text-emerald-700 font-gilroyBold ">
                      {quiz.points !== undefined && quiz.points !== null ? quiz.points : 2} Points
                    </strong>
                  </div>
                  <div>
                    <span className="text-gray-500">Display Order: </span>
                    <strong className="text-gray-800 font-gilroyBold">
                      #{quiz.orderIndex || idx + 1}
                    </strong>
                  </div>
                  <div>
                    <span className="text-gray-500">Lesson: </span>
                    <strong className="text-gray-800 font-gilroyBold">
                      {matchedLesson ? matchedLesson.title : "Unassigned"}
                    </strong>
                  </div>
                  <div>
                    <span className="text-gray-500">Status: </span>
                    <Tag color={quiz.isActive ? "green" : "red"}>
                      {quiz.isActive ? "Active" : "Inactive"}
                    </Tag>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    size="small"
                    type="primary"
                    icon={<EditOutlined />}
                    onClick={() => openEditQuizModal(quiz)}
                    className="bg-emerald-600 hover:bg-emerald-500 font-gilroyBold rounded-xl"
                  >
                    Edit Question
                  </Button>
                  <Popconfirm
                    title="Delete this quiz question?"
                    onConfirm={() => handleQuizDelete(quiz.id)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button size="small" danger icon={<DeleteOutlined />} className="rounded-xl">
                      Delete
                    </Button>
                  </Popconfirm>
                </div>
              </div>
            </div>
          </div>
        ),
      };
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 bg-gray-50 p-4 rounded-2xl border border-gray-200">
        <div>
          <h3 className="font-gilroyBold text-xl text-gray-900 m-0">
            Ambassador Training & Qualification
          </h3>
          <p className="text-gray-500  m-0 mt-0.5">
            Manage training video lessons and quiz questions with assigned scoring points.
          </p>
        </div>

        <div className="flex items-center gap-2">
          {activeSubTab === "lessons" ? (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openCreateModal}
              className="bg-emerald-600 hover:bg-emerald-500 font-gilroyBold rounded-xl h-10 px-4"
            >
              Add Lesson
            </Button>
          ) : (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openCreateQuizModal}
              className="bg-emerald-600 hover:bg-emerald-500 font-gilroyBold rounded-xl h-10 px-4"
            >
              Add Quiz Question
            </Button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 bg-gray-100 p-1.5 rounded-2xl w-fit">
        <button
          type="button"
          onClick={() => setActiveSubTab("lessons")}
          className={`px-4 py-2 text-[13px] rounded-xl  font-gilroyBold transition-all border-0 cursor-pointer ${activeSubTab === "lessons"
            ? "bg-white text-gray-900 shadow-xs"
            : "text-gray-600 hover:text-gray-900 bg-transparent"
            }`}
        >
          Video Lessons ({lessons.length})
        </button>
        <button
          type="button"
          onClick={() => setActiveSubTab("quiz")}
          className={`px-4 py-2 text-[13px] rounded-xl  font-gilroyBold transition-all border-0 cursor-pointer ${activeSubTab === "quiz"
            ? "bg-white text-gray-900 shadow-xs"
            : "text-gray-600 hover:text-gray-900 bg-transparent"
            }`}
        >
          Quiz Questions ({quizzes.length})
        </button>
      </div>

      {activeSubTab === "lessons" && (
        <>
          {loading ? (
            <div className="flex justify-center p-8">
              <Spin indicator={antIcon} />
            </div>
          ) : lessons.length === 0 ? (
            <Empty description="No ambassador lessons found. Click 'Add Lesson' to create one." />
          ) : (
            <Collapse items={renderLessonCollapseItems()} defaultActiveKey={[lessons[0]?.id]} />
          )}
        </>
      )}

      {activeSubTab === "quiz" && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4 bg-gray-50 p-3 rounded-2xl border border-gray-200">
            <div className="flex items-center gap-2">
              <span className="font-gilroyBold text-gray-700 text-sm">Filter by Lesson:</span>
              <Select
                value={selectedLessonFilter}
                onChange={setSelectedLessonFilter}
                className="min-w-[220px]"
                options={[
                  { label: `All Lessons (${quizzes.length})`, value: "ALL" },
                  ...lessons.map((l) => {
                    const count = quizzes.filter((q) => q.lessonId === l.id).length;
                    return {
                      label: `${l.orderIndex ? `${l.orderIndex}. ` : ""}${l.title} (${count})`,
                      value: l.id,
                    };
                  }),
                ]}
              />
            </div>
            <span className="text-gray-500 text-sm">
              Showing{" "}
              {
                (selectedLessonFilter === "ALL"
                  ? quizzes
                  : quizzes.filter((q) => q.lessonId === selectedLessonFilter)
                ).length
              }{" "}
              of {quizzes.length} question(s)
            </span>
          </div>

          {quizLoading ? (
            <div className="flex justify-center p-8">
              <Spin indicator={antIcon} />
            </div>
          ) : quizzes.length === 0 ? (
            <Empty description="No quiz questions configured. Click 'Add Quiz Question' to create questions and assign points (default 2 points)." />
          ) : (
            <Collapse items={renderQuizCollapseItems()} defaultActiveKey={[quizzes[0]?.id]} />
          )}
        </>
      )}

      {/* Lesson Create / Edit Modal */}
      <Modal
        title={
          <span className="font-gilroyBold text-lg">
            {editingLesson ? "Edit Ambassador Lesson" : "Add New Ambassador Lesson"}
          </span>
        }
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          form.resetFields();
          setEditingLesson(null);
        }}
        onOk={handleSubmit}
        confirmLoading={submitting}
        okText={editingLesson ? "Update Lesson" : "Create Lesson"}
        okButtonProps={{ className: "bg-emerald-600 hover:bg-emerald-500 font-gilroyBold rounded-xl" }}
        centered
        styles={{ body: { maxHeight: "calc(80vh - 80px)", overflowY: "auto" } }}
        cancelButtonProps={{ className: "rounded-xl" }}
      >
        <Form form={form} layout="vertical" className="mt-4 max-h-[calc(78vh-100px)] overflow-y-auto pr-2">
          <Form.Item
            name="title"
            label={<span className="font-gilroyBold text-gray-800">Lesson Title</span>}
            rules={[{ required: true, message: "Please input the lesson title" }]}
          >
            <Input
              placeholder="e.g. Konnect Ambassador Orientation & Earnings Guide"
              className="rounded-xl py-2"
            />
          </Form.Item>

          <Form.Item
            name="videoUrl"
            label={<span className="font-gilroyBold text-gray-800">Video Link (YouTube URL)</span>}
            rules={[{ required: true, message: "Please input the YouTube video link" }]}
            extra="Supported formats: https://youtu.be/... or https://www.youtube.com/watch?v=..."
          >
            <Input
              prefix={<VideoCameraOutlined className="text-gray-400" />}
              placeholder="https://youtu.be/84cr6ZSa6LM?si=UyAtWc7G7BkDOp4A"
              className="rounded-xl py-2"
            />
          </Form.Item>

          <Form.Item
            name="description"
            label={<span className="font-gilroyBold text-gray-800">Description / Overview</span>}
          >
            <TextArea
              rows={3}
              placeholder="Brief summary of what the ambassador will learn in this lesson..."
              className="rounded-xl"
            />
          </Form.Item>

          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              name="orderIndex"
              label={<span className="font-gilroyBold text-gray-800">Lesson Order</span>}
              rules={[{ required: true, message: "Required" }]}
            >
              <InputNumber min={1} max={50} className="w-full rounded-xl" />
            </Form.Item>

            <Form.Item
              name="isActive"
              label={<span className="font-gilroyBold text-gray-800">Active</span>}
              valuePropName="checked"
            >
              <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
            </Form.Item>
          </div>
        </Form>
      </Modal>

      {/* Quiz Question Create / Edit Modal */}
      <Modal
        title={
          <span className="font-gilroyBold text-lg">
            {editingQuiz ? "Edit Quiz Question" : "Add New Quiz Question"}
          </span>
        }
        open={quizModalOpen}
        onCancel={() => {
          setQuizModalOpen(false);
          quizForm.resetFields();
          setEditingQuiz(null);
        }}
        onOk={handleQuizSubmit}
        confirmLoading={quizSubmitting}
        okText={editingQuiz ? "Update Question" : "Create Question"}
        okButtonProps={{ className: "bg-emerald-600 hover:bg-emerald-500 font-gilroyBold rounded-xl" }}
        centered
        styles={{ body: { maxHeight: "calc(80vh - 80px)", overflowY: "auto" } }}
        cancelButtonProps={{ className: "rounded-xl" }}
        width={650}
      >
        <Form form={quizForm} layout="vertical" className="mt-4 max-h-[calc(78vh-100px)] overflow-y-auto pr-2">
          <Form.Item
            name="lessonId"
            label={<span className="font-gilroyBold text-gray-800">Associated Lesson</span>}
            rules={[{ required: true, message: "Please select the lesson this quiz question belongs to" }]}
          >
            <Select
              placeholder="Select associated lesson"
              className="w-full rounded-xl"
              options={lessons.map((l) => ({
                label: `${l.orderIndex ? `${l.orderIndex}. ` : ""}${l.title}`,
                value: l.id,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="question"
            label={<span className="font-gilroyBold text-gray-800">Question Prompt</span>}
            rules={[{ required: true, message: "Please input the question prompt" }]}
          >
            <TextArea
              rows={2}
              placeholder="e.g. How do Konnect Ambassadors earn weekly commissions?"
              className="rounded-xl"
            />
          </Form.Item>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Form.Item
              name="points"
              label={
                <div className="flex items-center gap-1.5">
                  <span className="font-gilroyBold text-gray-800">Assigned Points</span>
                  <span className=" text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md font-gilroyBold border border-emerald-200">
                    Default: 2
                  </span>
                </div>
              }
              rules={[{ required: true, message: "Required" }]}
              initialValue={2}
            >
              <InputNumber min={1} max={100} className="w-full rounded-xl" />
            </Form.Item>

            <Form.Item
              name="orderIndex"
              label={<span className="font-gilroyBold text-gray-800">Question Order</span>}
              rules={[{ required: true, message: "Required" }]}
            >
              <InputNumber min={1} max={50} className="w-full rounded-xl" />
            </Form.Item>
          </div>

          <div className="bg-gray-50 p-4 rounded-2xl border border-gray-200 space-y-3 mb-4">
            <div className="flex items-center justify-between">
              <span className="font-gilroyBold text-gray-900 ">
                4 Options & Select Correct Answer
              </span>
              <span className=" text-gray-500">Select which radio corresponds to the correct option</span>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Form.Item
                  name="option0"
                  label={<span className="font-gilroyBold text-gray-700 ">Option A</span>}
                  rules={[{ required: true, message: "Required" }]}
                  className="flex-1 m-0"
                >
                  <Input placeholder="Enter Option A" className="rounded-xl" />
                </Form.Item>
              </div>

              <div className="flex items-center gap-3">
                <Form.Item
                  name="option1"
                  label={<span className="font-gilroyBold text-gray-700 ">Option B</span>}
                  rules={[{ required: true, message: "Required" }]}
                  className="flex-1 m-0"
                >
                  <Input placeholder="Enter Option B" className="rounded-xl" />
                </Form.Item>
              </div>

              <div className="flex items-center gap-3">
                <Form.Item
                  name="option2"
                  label={<span className="font-gilroyBold text-gray-700 ">Option C</span>}
                  rules={[{ required: true, message: "Required" }]}
                  className="flex-1 m-0"
                >
                  <Input placeholder="Enter Option C" className="rounded-xl" />
                </Form.Item>
              </div>

              <div className="flex items-center gap-3">
                <Form.Item
                  name="option3"
                  label={<span className="font-gilroyBold text-gray-700 ">Option D</span>}
                  rules={[{ required: true, message: "Required" }]}
                  className="flex-1 m-0"
                >
                  <Input placeholder="Enter Option D" className="rounded-xl" />
                </Form.Item>
              </div>

              <Form.Item
                name="correctAnswer"
                label={<span className="font-gilroyBold text-emerald-800 ">Which option is correct?</span>}
                rules={[{ required: true, message: "Select the correct option" }]}
                initialValue={0}
                className="pt-2 m-0"
              >
                <Radio.Group className="flex flex-wrap gap-4">
                  <Radio value={0} className="font-gilroyBold">Option A</Radio>
                  <Radio value={1} className="font-gilroyBold">Option B</Radio>
                  <Radio value={2} className="font-gilroyBold">Option C</Radio>
                  <Radio value={3} className="font-gilroyBold">Option D</Radio>
                </Radio.Group>
              </Form.Item>
            </div>
          </div>

          <Form.Item
            name="explanation"
            label={<span className="font-gilroyBold text-gray-800">Explanation / Answer Note (Optional)</span>}
            extra="Shown to candidates after answering to explain why the correct choice is right."
          >
            <TextArea
              rows={2}
              placeholder="e.g. Ambassadors earn 1% cashback on referrals and 1.5% weekly on sales..."
              className="rounded-xl"
            />
          </Form.Item>

          <Form.Item
            name="isActive"
            label={<span className="font-gilroyBold text-gray-800">Active (Visible in Ambassador Quiz)</span>}
            valuePropName="checked"
          >
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Video Preview Modal */}
      <Modal
        title={<span className="font-gilroyBold text-lg">Video Preview</span>}
        open={Boolean(previewVideoUrl)}
        onCancel={() => setPreviewVideoUrl(null)}
        footer={null}
        width={640}
        destroyOnClose
      >
        {previewVideoUrl && (
          <div className="w-full aspect-video rounded-2xl overflow-hidden mt-2 bg-black">
            <iframe
              src={previewVideoUrl}
              title="Ambassador Lesson Preview"
              className="w-full h-full border-0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AdminAmbassadorLessons;
